import { Chart, TooltipModel } from 'chart.js';

const TOOLTIP_CLASS = 'hp-chart-tooltip';
const tooltipByParent = new WeakMap<HTMLElement, HTMLDivElement>();

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function shortTankName(label: string): string {
  return label
    .replace(/\s*\(.*\)/, '')
    .replace(/weighted.*/i, 'Weighted avg')
    .replace(/·.*/, '')
    .trim();
}

function isLoadSeries(label: string): boolean {
  return /weighted|load temp|avg|^plan [ab]$/i.test(label);
}

function colorFromPoint(p: {
  dataset: { borderColor?: unknown; backgroundColor?: unknown };
}): string {
  const bc = p.dataset.borderColor;
  const bg = p.dataset.backgroundColor;
  if (typeof bc === 'string') {
    return bc;
  }
  if (typeof bg === 'string') {
    return bg;
  }
  return '#94a3b8';
}

function isDayPanel(el: HTMLElement): boolean {
  return (
    el.classList.contains('hp-day-card__body') ||
    el.classList.contains('hp-day-card') ||
    el.classList.contains('hp-day-inspector') ||
    el.classList.contains('hp-inspector-float')
  );
}

function dayCardRoot(el: HTMLElement): HTMLElement | null {
  return el.closest('.hp-day-card') as HTMLElement | null;
}

function positionFloatingTooltip(
  tooltipEl: HTMLElement,
  chart: Chart,
  tooltip: TooltipModel<'bar' | 'line'>,
): void {
  const wrap = chart.canvas.parentElement;
  if (!wrap) {
    return;
  }

  const tipW = tooltipEl.offsetWidth || 260;
  const tipH = tooltipEl.offsetHeight || 120;

  let anchorX = wrap.clientWidth / 2;
  let anchorY = 24;

  const first = tooltip.dataPoints[0];
  if (first?.element) {
    const el = first.element as { x?: number; y?: number };
    if (typeof el.x === 'number' && typeof el.y === 'number') {
      anchorX = el.x;
      anchorY = el.y;
    }
  }

  let left = anchorX - tipW / 2;
  let top = anchorY - tipH - 22;

  left = Math.max(8, Math.min(left, wrap.clientWidth - tipW - 8));
  top = Math.max(8, Math.min(top, anchorY - tipH - 16));

  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
  tooltipEl.style.right = 'auto';
  tooltipEl.style.bottom = 'auto';
}

/** External tooltip — day card panel or compact chart overlay. */
export function createExternalTooltipHandler(
  containerEl: HTMLElement | null,
  unitOverride?: string,
): (context: { chart: Chart; tooltip: TooltipModel<'bar' | 'line'> }) => void {
  return (context) => {
    const parent = containerEl ?? context.chart.canvas.parentElement;
    if (!parent) {
      return;
    }

    const dayPanel = isDayPanel(parent);
    const cardRoot = dayCardRoot(parent);

    let tooltipEl = tooltipByParent.get(parent);
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.className = dayPanel
        ? `${TOOLTIP_CLASS} ${TOOLTIP_CLASS}--inspector`
        : TOOLTIP_CLASS;
      tooltipEl.setAttribute('role', 'status');
      if (!dayPanel) {
        parent.style.position = parent.style.position || 'relative';
      }
      parent.appendChild(tooltipEl);
      tooltipByParent.set(parent, tooltipEl);
    }

    const { tooltip, chart } = context;

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = '0';
      tooltipEl.style.visibility = 'hidden';
      tooltipEl.style.display = 'none';
      cardRoot?.classList.add('is-idle');
      return;
    }

    cardRoot?.classList.remove('is-idle');

    const unit =
      unitOverride ??
      (tooltip.dataPoints.some((p) => String(p.dataset.label).includes('°F'))
        ? '°F'
        : '°C');
    const date = tooltip.title?.[0] ?? 'Selected day';

    const points = [...tooltip.dataPoints]
      .filter((p) => {
        const y = p.parsed.y;
        return y !== null && y !== undefined && !Number.isNaN(y) && y > 0;
      })
      .sort((a, b) => {
        const aLoad = isLoadSeries(String(a.dataset.label ?? ''));
        const bLoad = isLoadSeries(String(b.dataset.label ?? ''));
        if (aLoad && !bLoad) {
          return -1;
        }
        if (!aLoad && bLoad) {
          return 1;
        }
        return (b.parsed.y ?? 0) - (a.parsed.y ?? 0);
      });

    const loadRows = points.filter((p) =>
      isLoadSeries(String(p.dataset.label ?? '')),
    );
    const tankRows = points.filter(
      (p) => !isLoadSeries(String(p.dataset.label ?? '')),
    );

    const loadHtml = loadRows
      .map(
        (p) => `<div class="hp-tt-hero-block">
        <span class="hp-tt-hero-lbl">${escapeHtml(shortTankName(String(p.dataset.label ?? '')))}</span>
        <span class="hp-tt-hero-num">${(p.parsed.y ?? 0).toFixed(1)}<em>${unit}</em></span>
      </div>`,
      )
      .join('');

    const tanksHtml = tankRows
      .map((p) => {
        const name = escapeHtml(shortTankName(String(p.dataset.label ?? '')));
        const val = (p.parsed.y ?? 0).toFixed(1);
        const color = colorFromPoint(p);
        return `<div class="hp-tt-row">
          <span class="hp-tt-dot" style="background:${color}"></span>
          <span class="hp-tt-name">${name}</span>
          <span class="hp-tt-val">${val}<small>${unit}</small></span>
        </div>`;
      })
      .join('');

    const count = points.length;
    const gridClass =
      dayPanel && tankRows.length > 1 ? ' hp-tt-tanks--grid' : tankRows.length > 3 ? ' hp-tt-tanks--grid' : '';

    tooltipEl.innerHTML = `
      <div class="hp-tt-head">
        <span class="hp-tt-date">${escapeHtml(date)}</span>
        <span class="hp-tt-count">${count} reading${count === 1 ? '' : 's'}</span>
      </div>
      ${loadHtml ? loadHtml : ''}
      ${tanksHtml ? `<div class="hp-tt-section hp-tt-tanks${gridClass}">${tanksHtml}</div>` : ''}
      ${!count ? '<div class="hp-tt-empty">No temperature data for this day</div>' : ''}
    `;

    tooltipEl.style.display = dayPanel ? 'flex' : 'block';
    tooltipEl.style.opacity = '1';
    tooltipEl.style.visibility = 'visible';

    if (!dayPanel) {
      positionFloatingTooltip(tooltipEl, chart, tooltip);
    }
  };
}
